import moment from 'moment'

/**
 * Invoice Date Calculation Utilities
 * Handles all date-related calculations for invoices
 */

class InvoiceDateCalculator {
  /**
   * Calculate due date from pay date and credit period
   */
  calculateDueDate(payDate, creditPeriod) {
    if (!payDate || !creditPeriod) {
      return null
    }

    const pay = moment(payDate)
    if (!pay.isValid()) {
      return null
    }

    return pay.clone().add(parseInt(creditPeriod), 'days').format('YYYY-MM-DD')
  }

  /**
   * Calculate billing month from invoice date
   */
  calculateBillingMonth(invoiceDate) {
    if (!invoiceDate) {
      return null
    }

    const date = moment(invoiceDate)
    if (!date.isValid()) {
      return null
    }

    return date.format('MMM-YYYY')
  }

  /**
   * Get invoice month from billing month or invoice date
   */
  getInvoiceMonth(billingMonth, invoiceDate) {
    if (billingMonth) {
      return billingMonth
    }

    if (invoiceDate) {
      return this.calculateBillingMonth(invoiceDate)
    }

    return null
  }

  /**
   * Check if invoice date matches billing month
   */
  isInvoiceDateInBillingMonth(invoiceDate, billingMonth) {
    if (!invoiceDate || !billingMonth) {
      return true // If either is missing, consider valid
    }

    const invoice = moment(invoiceDate)
    const billing = moment(billingMonth, 'MMM-YYYY')

    if (!invoice.isValid() || !billing.isValid()) {
      return false
    }

    return invoice.format('MMM-YYYY') === billing.format('MMM-YYYY')
  }

  /**
   * Get next notification date based on credit period and schedule
   */
  calculateNextNotificationDate(dueDate, notificationSchedule) {
    if (!dueDate || !notificationSchedule) {
      return null
    }

    const due = moment(dueDate)
    if (!due.isValid()) {
      return null
    }

    // notificationSchedule should be an array of days before due date
    // e.g., [30, 15, 7, 1] means notify 30, 15, 7, and 1 day before due date
    if (!Array.isArray(notificationSchedule) || notificationSchedule.length === 0) {
      return null
    }

    // Find the next notification date (first date in the future)
    const today = moment()
    for (const daysBefore of notificationSchedule.sort((a, b) => b - a)) {
      const notificationDate = due.clone().subtract(daysBefore, 'days')
      if (notificationDate.isAfter(today) || notificationDate.isSame(today, 'day')) {
        return notificationDate.format('YYYY-MM-DD')
      }
    }

    return null
  }

  /**
   * Calculate days until due date
   */
  getDaysUntilDue(dueDate) {
    if (!dueDate) {
      return null
    }

    const due = moment(dueDate)
    if (!due.isValid()) {
      return null
    }

    const today = moment()
    return due.diff(today, 'days')
  }

  /**
   * Check if invoice is overdue
   */
  isOverdue(dueDate) {
    const daysUntilDue = this.getDaysUntilDue(dueDate)
    return daysUntilDue !== null && daysUntilDue < 0
  }

  /**
   * Get e-invoice filing date based on schedule
   */
  getEInvoiceFilingDate(invoiceDate, filingSchedule) {
    if (!invoiceDate || !filingSchedule) {
      return null
    }

    const invoice = moment(invoiceDate)
    if (!invoice.isValid()) {
      return null
    }

    // filingSchedule should specify when to file (e.g., "end of month", "15th of next month")
    if (typeof filingSchedule === 'string') {
      if (filingSchedule === 'end_of_month') {
        return invoice.clone().endOf('month').format('YYYY-MM-DD')
      } else if (filingSchedule === '15th_next_month') {
        return invoice.clone().add(1, 'month').date(15).format('YYYY-MM-DD')
      }
    }

    // If it's a specific day of next month
    if (typeof filingSchedule === 'number') {
      return invoice.clone().add(1, 'month').date(filingSchedule).format('YYYY-MM-DD')
    }

    return null
  }

  /**
   * Check if booking check is due
   */
  isBookingCheckDue(invoiceDate, bookingCheckDays) {
    if (!invoiceDate || !bookingCheckDays) {
      return false
    }

    const invoice = moment(invoiceDate)
    if (!invoice.isValid()) {
      return false
    }

    const checkDate = invoice.clone().add(parseInt(bookingCheckDays), 'days')
    const today = moment()

    return today.isSameOrAfter(checkDate, 'day')
  }

  /**
   * Get booking check date
   */
  getBookingCheckDate(invoiceDate, bookingCheckDays) {
    if (!invoiceDate || !bookingCheckDays) {
      return null
    }

    const invoice = moment(invoiceDate)
    if (!invoice.isValid()) {
      return null
    }

    return invoice.clone().add(parseInt(bookingCheckDays), 'days').format('YYYY-MM-DD')
  }

  /**
   * Format date for display
   */
  formatDate(date, format = 'YYYY-MM-DD') {
    if (!date) {
      return ''
    }

    const momentDate = moment(date)
    if (!momentDate.isValid()) {
      return ''
    }

    return momentDate.format(format)
  }

  /**
   * Format invoice date for printed/preview sheets: DD-MON-YYYY
   * (e.g. 23-JUL-2026). Empty/invalid → ''.
   */
  formatInvoiceDisplayDate(date) {
    if (date === null || date === undefined || date === '') {
      return ''
    }

    if (date instanceof Date) {
      const fromDate = moment(date)
      return fromDate.isValid() ? fromDate.format('DD-MMM-YYYY').toUpperCase() : ''
    }

    const raw = String(date).trim()
    if (!raw) {
      return ''
    }

    // Prefer date-only parse for YYYY-MM-DD so local TZ does not shift the day.
    let momentDate
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      momentDate = moment(raw.slice(0, 10), 'YYYY-MM-DD', true)
    } else {
      momentDate = moment(raw, ['DD-MMM-YYYY', 'DD/MM/YYYY', 'DD-MM-YYYY', moment.ISO_8601], true)
    }

    if (!momentDate.isValid()) {
      return ''
    }

    return momentDate.format('DD-MMM-YYYY').toUpperCase()
  }

  /**
   * Parse date string to moment object
   */
  parseDate(dateString) {
    if (!dateString) {
      return null
    }

    const date = moment(dateString)
    return date.isValid() ? date : null
  }
}

export default new InvoiceDateCalculator()

const BILLING_MONTH_FORMAT = 'MMM-YYYY'
const BILLING_MONTH_PAST_COUNT = 6
const BILLING_MONTH_PARSE = [BILLING_MONTH_FORMAT, 'MMMM-YYYY', 'MMM YYYY', 'YYYY-MM']

/** Current calendar year/month in IST (Asia/Kolkata). */
export function getIstYearMonth(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: 'numeric',
  }).formatToParts(now)
  return {
    year: Number(parts.find((p) => p.type === 'year').value),
    monthIndex: Number(parts.find((p) => p.type === 'month').value) - 1,
  }
}

export function currentBillingMonthValue(now = new Date()) {
  const { year, monthIndex } = getIstYearMonth(now)
  return moment({ year, month: monthIndex, day: 1 }).format(BILLING_MONTH_FORMAT)
}

/**
 * Invoice Create Billing Month list: current IST month + previous 6.
 * Never offers other future months. `includeValue` keeps a draft/extracted
 * future month selected without adding more future months.
 */
export function buildBillingMonthOptions(includeValue, now = new Date()) {
  const { year, monthIndex } = getIstYearMonth(now)
  const options = []
  for (let i = BILLING_MONTH_PAST_COUNT; i >= 0; i -= 1) {
    const value = moment({ year, month: monthIndex - i, day: 1 }).format(BILLING_MONTH_FORMAT)
    options.push({ text: value, value })
  }
  const extra = includeValue != null ? String(includeValue).trim() : ''
  if (extra && !options.some((o) => o.value === extra)) {
    options.push({ text: extra, value: extra })
  }
  return options
}

/** If `value` is after the current IST month, return the current month. */
export function clampBillingMonthToCurrentOrPast(value, now = new Date()) {
  const current = currentBillingMonthValue(now)
  if (!value) return current
  const parsed = moment(String(value).trim(), BILLING_MONTH_PARSE, true)
  if (!parsed.isValid()) return current
  const currentM = moment(current, BILLING_MONTH_FORMAT)
  if (parsed.isAfter(currentM, 'month')) return current
  return parsed.format(BILLING_MONTH_FORMAT)
}
